import clsx from "clsx"
import { FC } from "react"
import { useTranslation } from "react-i18next"
import { SpinnerCircular } from "spinners-react"
import { LuckIcon, SpeedIcon, StaminaIcon } from "@/assets"
import { displayUsername } from "@/helpers/displayUsername"
import { numberFixed } from "@/helpers/numberFixed"
import { useModal } from "@/modals"
import { useGetPlayerInfoQuery } from "@/store/apis/playerAPI"
import { useGetLeaderboardDataQuery } from "@/store/apis/userAPI"
import { Button } from "@/ui/Button"
import { Modal } from "@/ui/Modal"
import { OutlinedText } from "@/ui/OutlinedText"
import { PlayerAvatarV2 } from "@/ui/PlayerAvatarV2"
import styles from "./MatchPlayerModal.module.scss"
import { Achievements, UpgradeCard } from "./ui"

const modalType = "matchPlayer"

export const MatchPlayerModal: FC = () => {
  const { t } = useTranslation()
  const { modal, getModalParams } = useModal(modalType)

  const { playerId } = getModalParams(modalType)

  const { data: playerInfo } = useGetPlayerInfoQuery(playerId)
  const { data: leaderboardData } = useGetLeaderboardDataQuery(
    playerInfo?.labrUser || "",
    {
      skip: !playerInfo?.labrUser
    }
  )

  const handleClose = () => {
    modal.close()
  }

  return (
    <Modal.Center
      type={modalType}
      className={clsx(styles.MatchPlayerModal)}
      body={{ className: styles.MatchPlayerModal_body }}
    >
      <Modal.StoneFlexContainer
        className={styles.MatchPlayerModal_container}
        content={{ className: styles.MatchPlayerModal_content }}
        outside={
          <>
            <div className={clsx(styles.MatchPlayerModal_footerButtons)}>
              <Button
                className={clsx(styles.MatchPlayerModal_footerButtons_item)}
                styles="green"
                onClick={handleClose}
              >
                {t("button.close")}
              </Button>
            </div>
          </>
        }
      >
        <div className={clsx(styles.MatchPlayerModal_header)}>
          <PlayerAvatarV2
            className={clsx(styles.MatchPlayerModal_avatar)}
            src={playerInfo?.visibleAvatar}
          />
          <OutlinedText className={clsx(styles.MatchPlayerModal_title)}>
            @{displayUsername(playerInfo?.visibleUsername)}
          </OutlinedText>
        </div>
        {leaderboardData ? (
          <>
            <div className={clsx(styles.MatchPlayerModal_info)}>
              <div className={clsx(styles.MatchPlayerModal_info_left)}>
                <div className={clsx(styles.MatchPlayerModal_info_header)}>
                  <div
                    className={clsx(
                      styles.MatchPlayerModal_infoBox,
                      styles.user
                    )}
                  >
                    <div className={clsx(styles.position)}>
                      #{leaderboardData?.position ?? 0}
                    </div>{" "}
                    <div className={clsx(styles.name)}>
                      <div>{playerInfo?.name}</div>
                    </div>
                  </div>
                  <div
                    className={clsx(
                      styles.MatchPlayerModal_infoBox,
                      styles.pit
                    )}
                  >
                    <div>{numberFixed(leaderboardData?.progress, 0)}m</div>
                    <OutlinedText className={clsx(styles.level)}>
                      {t("level")} {leaderboardData?.location}
                    </OutlinedText>
                  </div>
                </div>
                <div className={clsx(styles.MatchPlayerModal_separator)} />
                <div className={clsx(styles.MatchPlayerModal_upgrades)}>
                  {leaderboardData?.speedUpgrade && (
                    <UpgradeCard data={leaderboardData.speedUpgrade} />
                  )}
                  {leaderboardData?.luckUpgrade && (
                    <UpgradeCard data={leaderboardData.luckUpgrade} />
                  )}
                  {leaderboardData?.staminaUpgrade && (
                    <UpgradeCard data={leaderboardData.staminaUpgrade} />
                  )}
                </div>
              </div>
              <div className={clsx(styles.MatchPlayerModal_info_right)}>
                <div className={clsx(styles.MatchPlayerModal_shovel)}>
                  <div className={clsx(styles.MatchPlayerModal_shovel_top)}>
                    <div className={clsx(styles.MatchPlayerModal_shovel_icon)}>
                      <img
                        src={leaderboardData?.activeTool.activeTool.imgSrc}
                        alt="shovel"
                      />
                    </div>
                    <div className={clsx(styles.MatchPlayerModal_shovel_stats)}>
                      <div
                        className={clsx(
                          styles.MatchPlayerModal_shovel_stats_item
                        )}
                      >
                        <SpeedIcon />{" "}
                        {numberFixed(
                          leaderboardData?.activeTool.activeTool.speedBonus,
                          2
                        )}
                      </div>
                      <div
                        className={clsx(
                          styles.MatchPlayerModal_shovel_stats_item
                        )}
                      >
                        <LuckIcon />{" "}
                        {numberFixed(
                          leaderboardData?.activeTool.activeTool.luckBonus,
                          2
                        )}
                      </div>
                      <div
                        className={clsx(
                          styles.MatchPlayerModal_shovel_stats_item
                        )}
                      >
                        <StaminaIcon />{" "}
                        {numberFixed(
                          leaderboardData?.activeTool.activeTool.enduranceBonus
                        )}
                      </div>
                    </div>
                  </div>
                  <div className={clsx(styles.MatchPlayerModal_shovel_runes)}>
                    {Array.from({ length: 6 }).map((_, i) => {
                      const rune =
                        leaderboardData?.activeTool.runesInActiveTool[i]
                      return (
                        <div
                          className={clsx(styles.MatchPlayerModal_shovel_rune, {
                            [styles.active]: !!rune
                          })}
                          key={i}
                        >
                          {rune && <img src={rune.imgSrc} alt={rune.type} />}
                        </div>
                      )
                    })}
                  </div>
                </div>
                {leaderboardData?.achievements && (
                  <Achievements
                    achievements={leaderboardData?.achievements}
                    stakingBonus={leaderboardData?.stakingBonus}
                  />
                )}
              </div>
            </div>
          </>
        ) : (
          <div className={clsx(styles.MatchPlayerModal_info)}>
            <SpinnerCircular size={32} color="#35262699" />
          </div>
        )}
      </Modal.StoneFlexContainer>
    </Modal.Center>
  )
}
